(() => {
  const cm = value => Math.round(Number(value) * 100);
  const format = value => new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(value);
  const fits = (sheet, a, b) => (sheet.width >= a && sheet.length >= b) || (sheet.width >= b && sheet.length >= a);
  function nextSize(current, catalog) {
    return catalog.filter(item => item.kind === 'liner' && fits(item, current.width, current.length) && item.width * item.length > current.width * current.length).sort((x, y) => x.width * x.length - y.width * y.length || x.price - y.price || x.id.localeCompare(y.id))[0];
  }
  function sizeChoices(first, catalog) {
    const choices = [];
    let current = first;
    while (current) { choices.push(current); current = nextSize(current, catalog); }
    return choices;
  }
  function calculate(values, catalog) {
    const { length, width, depth, edge } = values;
    if (![length, width, depth, edge].every(Number.isFinite) || length <= 0 || width <= 0 || depth <= 0 || length > 100 || width > 100 || depth > 5 || edge < 0 || edge > 2) throw new Error('Invalid dimensions');
    const a = cm(length) + 2 * cm(depth) + 2 * cm(edge);
    const b = cm(width) + 2 * cm(depth) + 2 * cm(edge);
    const liners = catalog.filter(item => item.kind === 'liner' && fits(item, a, b)).sort((x, y) => x.width * x.length - y.width * y.length || x.price - y.price || x.id.localeCompare(y.id));
    return { a: a / 100, b: b / 100, best: liners[0] };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculate, fits, nextSize, sizeChoices };
  if (typeof customElements === 'undefined' || customElements.get('eot-pond-sizing')) return;
  customElements.define('eot-pond-sizing', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      const form = this.querySelector('form');
      const result = this.querySelector('[role=status]');
      const strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      const catalog = [...this.querySelectorAll('[data-catalog] a')].map(anchor => ({ ...anchor.dataset, width: cm(anchor.dataset.width), length: cm(anchor.dataset.length), price: Number(anchor.dataset.price), available: anchor.dataset.available === 'true', url: anchor.getAttribute('href') }));
      const t = (key, replacements = {}) => Object.entries(replacements).reduce((text, [name, value]) => text.replaceAll('{' + name + '}', String(value)), strings[key]);
      const node = (tag, text, className) => { const item = document.createElement(tag); if (text) item.textContent = text; if (className) item.className = className; return item; };
      const update = () => {
        result.hidden = true;
        if (!form.reportValidity()) return;
        const values = Object.fromEntries([...form.querySelectorAll('input')].map(input => [input.name, input.value === '' ? NaN : Number(input.value)]));
        let answer;
        try { answer = calculate(values, catalog); } catch { result.replaceChildren(node('p', strings.invalid)); result.hidden = false; return; }
        const calculation = node('div', '', 'eot-sizing__estimate');
        calculation.append(node('h3', strings.result_heading), node('p', `${format(answer.a)}m × ${format(answer.b)}m`, 'eot-sizing__dimension'));
        const recommendation = node('div', '', 'eot-sizing__recommendation');
        recommendation.append(node('h3', strings.recommended));
        if (answer.best) {
          const choices = sizeChoices(answer.best, catalog);
          let selected = 0;
          const heading = node('h3', strings.recommended);
          const carousel = node('div', '', 'eot-sizing__carousel');
          const productHost = node('div', '', 'eot-sizing__product-host');
          const arrow = (direction, label) => {
            const wrapper = node('span', '', 'eot-sizing__arrow-wrap');
            const button = node('button', '', 'eot-sizing__arrow');
            button.type = 'button'; button.dataset.direction = direction;
            button.setAttribute('aria-label', label);
            const glyph = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            glyph.setAttribute('viewBox', '0 0 20 32'); glyph.setAttribute('width', '20'); glyph.setAttribute('height', '32'); glyph.setAttribute('aria-hidden', 'true');
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', direction === 'previous' ? 'M14 6 L4 16 L14 26' : 'M6 6 L16 16 L6 26');
            path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '1.5'); path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round');
            glyph.append(path); button.append(glyph);
            const tooltip = node('span', label, 'eot-sizing__tooltip eot-sizing__tooltip--' + direction);
            tooltip.id = this.id + '-' + direction + '-tooltip'; tooltip.setAttribute('role', 'tooltip');
            button.setAttribute('aria-describedby', tooltip.id);
            button.addEventListener('keydown', event => {
              if (event.key === 'Escape') { wrapper.dataset.dismissed = 'true'; event.stopPropagation(); }
            });
            wrapper.addEventListener('mouseleave', () => delete wrapper.dataset.dismissed);
            button.addEventListener('focus', () => delete wrapper.dataset.dismissed);
            wrapper.append(button, tooltip);
            return { wrapper, button };
          };
          const previous = arrow('previous', strings.previous_size);
          const next = arrow('next', strings.next_size);
          carousel.append(previous.wrapper, productHost, next.wrapper);
          recommendation.replaceChildren(heading, carousel);
          const showProduct = control => {
            const hadFocus = control && document.activeElement === control;
            const item = choices[selected];
            heading.textContent = strings[selected === 0 ? 'recommended' : 'selected'];
            const name = item.title;
            const card = node('a', '', 'eot-sizing__product' + (item.available ? '' : ' eot-sizing__product--sold-out')); card.href = item.url;
            const media = node('span', '', 'eot-sizing__product-media');
            if (!item.available) media.append(node('span', strings.sold_out, 'eot-sizing__badge'));
            if (item.image) { const image = document.createElement('img'); image.src = item.image; image.alt = ''; image.width = 112; image.height = 112; image.loading = 'lazy'; media.append(image); }
            card.append(media);
            const copy = node('div', '', 'eot-sizing__product-copy');
            copy.append(node('p', name, 'eot-sizing__product-name'), node('p', item.priceLabel, 'eot-sizing__price'));
            if (item.available) copy.append(node('p', strings.in_stock, 'eot-sizing__stock'));
            copy.append(node('span', strings.view_product + ' →', 'eot-sizing__product-link'));
            card.append(copy); productHost.replaceChildren(card);
            previous.button.disabled = selected === 0;
            previous.wrapper.hidden = selected === 0;
            carousel.classList.toggle('eot-sizing__carousel--first', selected === 0);
            next.button.disabled = selected === choices.length - 1;
            if (hadFocus && control.disabled) {
              const other = control === next.button ? previous.button : next.button;
              (other.disabled ? card : other).focus({ preventScroll: true });
            }
          };
          previous.button.addEventListener('click', () => { if (selected > 0) { selected--; showProduct(previous.button); } });
          next.button.addEventListener('click', () => { if (selected < choices.length - 1) { selected++; showProduct(next.button); } });
          showProduct();
        }
        else {
          const contact = node('a', strings.contact); contact.href = this.dataset.contactUrl;
          recommendation.append(node('p', strings.no_match), contact);
        }
        result.replaceChildren(calculation, recommendation);
        this.querySelector('[data-diagram-length]').textContent = `${format(values.length)}m`;
        this.querySelector('[data-diagram-depth]').textContent = `${format(values.depth)}m`;
        this.querySelector('[data-diagram-sheet]').textContent = t('diagram_result', { size: format(answer.a) });
        result.hidden = false;
      };
      form.addEventListener('submit', event => { event.preventDefault(); update(); });
      form.addEventListener('input', () => { result.hidden = true; });
    }
  });
})();
